'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdMedicalServices,
    MdClose,
    MdCheckCircle,
    MdCancel,
    MdBusiness,
    MdHistory,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    getTissConfigurations,
    deleteTissConfiguration,
    getTissConfigurationsByCompany,
    TissConfiguration,
} from '@/lib/api/health-integrations/tiss-api'
import { ShareableModule } from '@/lib/api/share/share-api'
import { MODULE_LABELS } from '@/hooks/useShareData'
import { ImportDataModal } from '@/components/import/ImportDataModal'
import { MdFileDownload } from 'react-icons/md'

export default function TissPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'health_integrations',
        'tiss'
    )

    const [configurations, setConfigurations] = useState<TissConfiguration[]>(
        []
    )
    const [filteredConfigurations, setFilteredConfigurations] = useState<
        TissConfiguration[]
    >([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [configToDelete, setConfigToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [showImportModal, setShowImportModal] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadConfigurations()
        }
    }, [user?.companyId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = configurations.filter(
                config =>
                    config.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    config.ansCode
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    config.cnpj.includes(searchTerm)
            )
            setFilteredConfigurations(filtered)
        } else {
            setFilteredConfigurations(configurations)
        }
    }, [searchTerm, configurations])

    const loadConfigurations = async () => {
        setLoading(true)
        try {
            const response = await getTissConfigurations()
            if (response.success) {
                setConfigurations(response.result)
                setFilteredConfigurations(response.result)
            } else {
                toast.error(
                    response.error || 'Erro ao carregar configurações TISS'
                )
            }
        } catch (error) {
            toast.error('Erro ao carregar configurações TISS')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (id: number) => {
        setConfigToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!configToDelete) return

        setDeleting(true)
        try {
            const response = await deleteTissConfiguration(configToDelete)
            if (response.success) {
                toast.success('Configuração excluída com sucesso')
                loadConfigurations()
                setShowDeleteModal(false)
                setConfigToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir configuração')
            }
        } catch (error) {
            toast.error('Erro ao excluir configuração')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando configurações TISS...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                        <MdMedicalServices className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            TISS/ANS
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {configurations.length}{' '}
                            {configurations.length === 1
                                ? 'configuração'
                                : 'configurações'}{' '}
                            cadastradas
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={() =>
                            navigate('/health-integrations/tiss/transactions')
                        }
                    >
                        <MdHistory className="text-xl" />
                        <span className="hidden sm:inline">Transações</span>
                    </button>
                    <button
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={() =>
                            navigate('/health-integrations/tiss/credentials')
                        }
                    >
                        <MdBusiness className="text-xl" />
                        <span className="hidden sm:inline">Credenciais</span>
                    </button>
                    <button
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={() => setShowImportModal(true)}
                    >
                        <MdFileDownload className="text-xl" />
                        <span className="hidden sm:inline">Importar</span>
                    </button>
                    {canCreate && (
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={() =>
                                navigate('/health-integrations/tiss/new')
                            }
                        >
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">Adicionar</span>
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, código ANS ou CNPJ..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredConfigurations.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdMedicalServices className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhuma configuração encontrada'
                                : 'Nenhuma configuração TISS cadastrada'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Configure sua empresa para trocar informações com operadoras'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/health-integrations/tiss/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar configuração
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {filteredConfigurations.map(config => (
                        <div
                            key={config.id}
                            className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300"
                        >
                            <div className="flex items-start gap-4 p-4">
                                <div className="relative w-16 h-16 flex-shrink-0 bg-gradient-to-br from-green-500/20 to-green-500/10 rounded-xl flex items-center justify-center border border-green-500/20">
                                    <MdMedicalServices className="text-3xl text-green-500" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-base font-bold text-base-content break-words">
                                                    {config.name}
                                                </h3>
                                                {config.active ? (
                                                    <span className="badge badge-success badge-sm gap-1">
                                                        <MdCheckCircle className="text-xs" />
                                                        Ativa
                                                    </span>
                                                ) : (
                                                    <span className="badge badge-ghost badge-sm gap-1">
                                                        <MdCancel className="text-xs" />
                                                        Inativa
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-base-content/70 mt-1">
                                                CNPJ: {config.cnpj} | CNES:{' '}
                                                {config.cnes}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2 mb-3">
                                        <span className="badge badge-outline badge-sm">
                                            ANS: {config.ansCode}
                                        </span>
                                        <span className="badge badge-outline badge-sm">
                                            TISS {config.tissVersion}
                                        </span>
                                        <span
                                            className={`badge badge-sm ${config.environment === 'PRODUCTION' ? 'badge-success' : 'badge-warning'}`}
                                        >
                                            {config.environment === 'PRODUCTION'
                                                ? 'Produção'
                                                : 'Homologação'}
                                        </span>
                                        {config._count && (
                                            <>
                                                <span className="badge badge-outline badge-sm">
                                                    {config._count.credentials}{' '}
                                                    credenciais
                                                </span>
                                                <span className="badge badge-outline badge-sm">
                                                    {config._count.transactions}{' '}
                                                    transações
                                                </span>
                                            </>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                        <div className="flex items-center gap-2 text-xs text-base-content/60">
                                            <span>
                                                Criada em{' '}
                                                {new Date(
                                                    config.createdAt
                                                ).toLocaleDateString('pt-BR')}
                                            </span>
                                        </div>

                                        {(canUpdate || canDelete) && (
                                            <div className="flex items-center gap-2">
                                                {canUpdate && (
                                                    <button
                                                        onClick={() =>
                                                            navigate(
                                                                `/health-integrations/tiss/edit/${config.id}`
                                                            )
                                                        }
                                                        className="btn btn-sm btn-ghost"
                                                        title="Editar"
                                                    >
                                                        <MdEdit className="text-lg" />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button
                                                        onClick={() =>
                                                            handleDelete(
                                                                config.id
                                                            )
                                                        }
                                                        className="btn btn-sm btn-ghost text-error hover:bg-error/10"
                                                        title="Excluir"
                                                    >
                                                        <MdDelete className="text-lg" />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.TISS_CONFIG}
                moduleLabel={MODULE_LABELS[ShareableModule.TISS_CONFIG]}
                hideFileImport
                fetchItems={async (companyId: number) => {
                    const response =
                        await getTissConfigurationsByCompany(companyId)
                    return response.success ? response.result : []
                }}
                renderItem={(item: TissConfiguration) => (
                    <div>
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-base-content/60">
                            ANS: {item.ansCode} | CNES: {item.cnes}
                            {item.active && (
                                <span className="badge badge-success badge-xs ml-2">
                                    Ativa
                                </span>
                            )}
                        </div>
                    </div>
                )}
                getItemId={(item: TissConfiguration) => item.id}
                getItemName={(item: TissConfiguration) => item.name}
                onImportComplete={() => loadConfigurations()}
            />

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setConfigToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir configuração"
                message="Tem certeza de que deseja excluir esta configuração TISS? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
